import { useMemo, useState } from 'react'
import CanvaFolderPicker from './CanvaFolderPicker.jsx'
import { BEZEL_SOURCES } from './folders.js'
import LogoCPicker from './LogoCPicker.jsx'
import { KIND } from './steps.js'
import { optionsFor, VIBRANTIUM_ID_LABEL, allowsVibrantiumId } from './studioConfig.js'
import { suggestAttributes } from './buildPayload.js'
import { sourcesForStep } from './stages.js'
import MarksLibrary from '../MarksLibrary.jsx'
import CanvaPicker from '../CanvaPicker.jsx'
import styles from './Studio.module.css'

/* ---------------------------------------------------------------- *
 * Small primitives
 * ---------------------------------------------------------------- */

/**
 * THE "ADD TO THE AI SCRIPT" BOX (Master Spec Section 2 — the second half of
 * every step). Its text posts under `scripts[stepKey]` and the backend's
 * Layer 3 appends it to THAT step's slot only, changing nothing else.
 */
function ScriptBox({ stepKey, value, onChange, passthrough }) {
  return (
    <div className={styles.script}>
      <label className="field-label" htmlFor={`script-${stepKey}`}>
        Add to the AI script
        {passthrough && <span className={styles.flag} title="Not one of the engine config's named script steps — the backend's Layer 3 passthrough carries it">passthrough</span>}
      </label>
      <textarea
        id={`script-${stepKey}`}
        className={`input ${styles.textarea}`}
        rows={2}
        placeholder="Targeted direction for this step only — e.g. “add the 45-18 record at the base,” “widen the starburst,” “HC under the coach name.”"
        value={value || ''}
        onChange={(e) => onChange(stepKey, e.target.value)}
      />
    </div>
  )
}

function ChipRow({ options, value, onPick, multi = false }) {
  const sel = multi ? new Set(value || []) : null
  return (
    <div className={styles.chipRow}>
      {options.map((o) => {
        const id = typeof o === 'string' ? o : o.id
        const label = typeof o === 'string' ? o : o.label
        const active = multi ? sel.has(id) : value === id
        return (
          <button
            key={id}
            type="button"
            className={`${styles.opt} ${active ? styles.optOn : ''}`}
            onClick={() => onPick(id)}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className={styles.field}>
      <div className="field-label">{label}</div>
      {children}
      {hint && <p className={styles.note}>{hint}</p>}
    </div>
  )
}

/* ---------------------------------------------------------------- *
 * The step body
 * ---------------------------------------------------------------- */

export default function StudioStep({ step, state, ctl, config, context }) {
  const c = state.components
  const k = state.keyed

  // The number, the title and the one-line "what happens next" live on the
  // card's own accordion header (Studio.jsx) so a COLLAPSED step still states
  // its move. Repeating them inside the panel would just be noise — and on a
  // phone, noise is scroll.
  return (
    <section className={styles.step}>
      <p className={styles.stepNext}>{step.next}</p>
      {step.why && <p className={styles.stepWhy}>{step.why}</p>}

      <div className={styles.stepBody}>
        <StepControl step={step} state={state} ctl={ctl} config={config} context={context} c={c} k={k} />
      </div>

      <SourceStrip stepKey={step.key} school={context.school} />

      <ScriptBox
        stepKey={step.key}
        value={state.scripts[step.key]}
        onChange={ctl.setScript}
        passthrough={step.passthrough}
      />
    </section>
  )
}

/**
 * THE FORMER TABS, WHERE THEY BELONG.
 *
 * Marks Library and Elements used to be two of the four top-level tabs, level
 * with "Build" — which is what made the studio read as four tools instead of
 * one. Neither is a destination: they are SOURCES that feed a step, and the
 * step they feed already has its own picker. What each one still has that the
 * inline picker does not is declared on the source (`stages.js#SOURCES.unique`)
 * and printed here, because a panel that adds nothing should have been deleted
 * rather than relocated.
 *
 * COLLAPSED BY DEFAULT, and that is a mobile-first decision as much as an
 * information one: an open Canva navigator inside step 2 would put a design
 * grid between the operator and step 3. A closed disclosure costs one 44px row.
 */
function SourceStrip({ stepKey, school }) {
  const [open, setOpen] = useState('')
  const sources = sourcesForStep(stepKey)
  if (!sources.length) return null

  return (
    <div className={styles.sourceStrip}>
      <div className={styles.tabs}>
        {sources.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`${styles.tab} ${open === s.id ? styles.tabOn : ''}`}
            aria-expanded={open === s.id}
            aria-controls={`source-${stepKey}-${s.id}`}
            onClick={() => setOpen((o) => (o === s.id ? '' : s.id))}
            title={s.unique}
          >
            {s.open}
            <span className={styles.chev} aria-hidden>{open === s.id ? '▴' : '▾'}</span>
          </button>
        ))}
      </div>
      {sources.map((s) => (open === s.id ? (
        <div key={s.id} id={`source-${stepKey}-${s.id}`} className={styles.sourceBody}>
          <p className={styles.note}><strong>{s.label}.</strong> {s.unique}.</p>
          {s.id === 'marks' && <MarksLibrary prefillName={school?.name} />}
          {s.id === 'elements' && <CanvaPicker />}
        </div>
      ) : null))}
    </div>
  )
}

function StepControl({ step, state, ctl, config, context, c, k }) {
  switch (step.kind) {
    case KIND.LOGO:
      return (
        <LogoCPicker
          value={state.logoC}
          onChange={ctl.setLogoC}
          onSchool={ctl.setSchool}
          school={context.school}
        />
      )

    case KIND.CANVA:
      // The bezel step has TWO libraries behind it; every other step has one.
      if (step.key === 'bezel') {
        return (
          <>
            <BezelSourcePicker c={c} ctl={ctl} step={step} />
            <BezelExtras c={c} ctl={ctl} config={config} />
          </>
        )
      }
      return (
        <>
          <CanvaFolderPicker
            folderKey={step.folderKey}
            value={c[step.key]}
            onChange={(v) => ctl.setComponent(step.key, v)}
            label={step.title}
          />
          {step.key === 'shank1' && (
            <Field label="Panel A — exact keyed text" hint="Rendered verbatim. Typed, never picked, never AI.">
              <input className="input" value={k.shank1Text} onChange={(e) => ctl.setKeyed('shank1Text', e.target.value)} placeholder="e.g. CHANTICLEERS / 2026" />
            </Field>
          )}
          {step.key === 'shank2' && (
            <>
              <Field label="Panel B — exact keyed text" hint="Rendered verbatim.">
                <input className="input" value={k.shank2Text} onChange={(e) => ctl.setKeyed('shank2Text', e.target.value)} placeholder="e.g. COASTAL CAROLINA / BASEBALL" />
              </Field>
              <Field label="Shank extras" hint="Appendix A places these on the right shank — the record along the base, “HC,” secondary marks.">
                <input className="input" value={c.shankExtras || ''} onChange={(e) => ctl.setComponent('shankExtras', e.target.value)} placeholder="e.g. record along the base, bronze crest" />
              </Field>
            </>
          )}
        </>
      )

    case KIND.CHOICE:
      return (
        <Field label="Stone rows below the bezel">
          <ChipRow options={optionsFor(config, 'girdleRows')} value={c.girdle} onPick={(v) => ctl.setComponent('girdle', v)} />
        </Field>
      )

    case KIND.TITLE:
      return <TitleStep ctl={ctl} context={context} k={k} />

    case KIND.TEXT:
      if (step.key === 'palm') {
        return (
          <Field label="Interior band text" hint="The inside-band story — the playoff ladder, the motto. Blank renders clean, unengraved metal.">
            <textarea className={`input ${styles.textarea}`} rows={3} value={k.palmText} onChange={(e) => ctl.setKeyed('palmText', e.target.value)} placeholder="e.g. QUARTERS 6–2 · SEMIS 4–1 · FINAL 9–3" />
          </Field>
        )
      }
      if (step.key === 'core') {
        return (
          <>
            <Field label="Core / interior engraving" hint="Described to the engine — the treatment, not the exact type.">
              <input className="input" value={c.coreEngraving || ''} onChange={(e) => ctl.setComponent('coreEngraving', e.target.value)} placeholder="e.g. elaborate scrollwork around the inner band" />
            </Field>
            <Field label="Signature line — exact keyed text" hint="Rendered verbatim, like every other keyed string.">
              <input className="input" value={k.signature} onChange={(e) => ctl.setKeyed('signature', e.target.value)} placeholder="e.g. Coach T. Mabry" />
            </Field>
          </>
        )
      }
      return (
        <Field label={step.title}>
          <input className="input" value={state.scripts[step.key] || ''} onChange={(e) => ctl.setScript(step.key, e.target.value)} />
        </Field>
      )

    case KIND.STONES:
      return <StonesStep c={c} ctl={ctl} config={config} />

    case KIND.METAL:
      return <MetalStep c={c} ctl={ctl} config={config} context={context} />

    case KIND.SCALE:
      return (
        <Field label="Physical size" hint="Larger sizes get a heavier shank, a taller bezel wall and an on-hand true-scale cue so a 6XL reads as the statement piece it is.">
          <ChipRow options={config.scales} value={state.scale} onPick={(v) => ctl.setTop('scale', v)} />
        </Field>
      )

    case KIND.COMPLEXITY:
      return (
        <Field label="Complexity 1–5" hint="1 = clean and restrained · 5 = fully iced, Super-Bowl-grade.">
          <ChipRow
            options={(config.complexity || []).map((n) => ({ id: n, label: String(n) }))}
            value={state.complexity}
            onPick={(v) => ctl.setTop('complexity', Number(v))}
          />
        </Field>
      )

    case KIND.BACKGROUND:
      return <BackgroundStep ctl={ctl} context={context} c={c} k={k} />

    default:
      return null
  }
}

/* ---------------------------------------------------------------- *
 * Step 2 extras — the action bezel is the innovation lever (Section 7)
 * ---------------------------------------------------------------- */

function BezelExtras({ c, ctl, config }) {
  const ab = c.actionBezel || {}
  const opens = ab.mode && ab.mode !== 'static'
  return (
    <>
      <Field label="Hero emblem on the face" hint="Optional. Drops out of the prompt entirely when blank — never rendered as an empty panel.">
        <input className="input" value={c.heroEmblem || ''} onChange={(e) => ctl.setComponent('heroEmblem', e.target.value)} placeholder="e.g. a sculpted championship trophy in graduated diamonds" />
      </Field>
      <Field label="Action bezel" hint="Static, or it opens. This is the innovation lever — the exploded view is what shows the mechanism.">
        <ChipRow options={config.actionBezelModes} value={ab.mode || 'static'} onPick={(v) => ctl.setActionBezel({ mode: v })} />
      </Field>
      {opens && (
        <div className={styles.two}>
          <Field label="What it reveals">
            <input className="input" value={ab.reveal || ''} onChange={(e) => ctl.setActionBezel({ reveal: e.target.value })} placeholder="e.g. a spinning puck insert over a secondary logo bezel" />
          </Field>
          <Field label="Insert engraving">
            <input className="input" value={ab.insert || ''} onChange={(e) => ctl.setActionBezel({ insert: e.target.value })} placeholder="e.g. 2026 STANLEY CUP" />
          </Field>
        </div>
      )}
    </>
  )
}

/* ---------------------------------------------------------------- *
 * Step 4 — Title + variable attributes. SUGGEST, DON'T AUTOFILL.
 * ---------------------------------------------------------------- */

function TitleStep({ ctl, context, k }) {
  const suggestions = useMemo(() => suggestAttributes(context), [context])
  const [draft, setDraft] = useState('')

  const byField = (field) => suggestions.filter((s) => s.field === field)
  const applied = (s) =>
    s.field === 'names' ? (k.names || []).includes(s.value) : k[s.field] === s.value

  function apply(s) {
    if (s.field === 'names') ctl.addName(s.value)
    else ctl.setKeyed(s.field, s.value)
  }

  return (
    <>
      <div className={styles.two}>
        <Field label="Title — top of the bezel (required)" hint="You write this. It is rendered verbatim, so it says what the client cares about — “CWS” over the conference title.">
          <input className="input" value={k.titleTop} onChange={(e) => ctl.setKeyed('titleTop', e.target.value)} placeholder="e.g. CWS" />
        </Field>
        <Field label="Title — bottom of the bezel">
          <input className="input" value={k.titleBottom} onChange={(e) => ctl.setKeyed('titleBottom', e.target.value)} placeholder="e.g. FINALIST" />
        </Field>
      </div>

      <Suggestions rows={[...byField('titleTop'), ...byField('titleBottom')]} applied={applied} apply={apply} label="Title suggestions" />

      <Field label="Season record" hint="Rendered verbatim wherever the shank script places it.">
        <input className="input" value={k.record} onChange={(e) => ctl.setKeyed('record', e.target.value)} placeholder="e.g. 45-18" />
      </Field>
      <Suggestions rows={byField('record')} applied={applied} apply={apply} label="Record suggestions" />

      <Field
        label="Variable attributes"
        hint="Every one of these renders verbatim. Include a suggestion or write your own — nothing is added for you."
      >
        {(k.names || []).length > 0 && (
          <ul className={styles.attrList}>
            {k.names.map((n, i) => (
              <li key={n + i} className={styles.attr}>
                <input
                  className="input"
                  value={n}
                  onChange={(e) => ctl.setName(i, e.target.value)}
                />
                <button type="button" className={styles.linkBtn} onClick={() => ctl.removeName(i)}>Remove</button>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.fallbackRow}>
          <input
            className="input"
            value={draft}
            placeholder="Add an attribute — coach name, mascot, conference…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) { e.preventDefault(); ctl.addName(draft.trim()); setDraft('') } }}
          />
          <button type="button" className="btn" disabled={!draft.trim()} onClick={() => { ctl.addName(draft.trim()); setDraft('') }}>Add</button>
        </div>
      </Field>

      <Suggestions rows={byField('names')} applied={applied} apply={apply} label="Attribute suggestions" />
    </>
  )
}

function Suggestions({ rows, applied, apply, label }) {
  if (!rows.length) {
    return (
      <p className={styles.note}>
        No suggestions available — pick a championship above, or a school in step 1, and the scrape / HubSpot / universe
        candidates appear here. Nothing is ever written for you.
      </p>
    )
  }
  return (
    <div className={styles.suggest}>
      <div className="field-label">{label} — click to include</div>
      <div className={styles.chipRow}>
        {rows.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`${styles.sugg} ${applied(s) ? styles.suggOn : ''}`}
            onClick={() => apply(s)}
            title={`from ${s.source}`}
          >
            <span className={styles.suggLabel}>{s.label}</span>
            <span className={styles.suggValue}>{s.value}</span>
            {applied(s) && <span className={styles.suggTick} aria-hidden>✓</span>}
          </button>
        ))}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- *
 * Step 9 / 10
 * ---------------------------------------------------------------- */

function StonesStep({ c, ctl, config }) {
  const st = c.stones || {}
  return (
    <>
      <Field label="Feature shapes" hint="Multi-select.">
        <ChipRow multi options={optionsFor(config, 'stoneShapes')} value={st.shapes} onPick={(v) => ctl.toggleStone('shapes', v)} />
      </Field>
      <Field label="Setting" hint="Channel-set · pavé · halo · picture-frame border. Multi-select.">
        <ChipRow multi options={optionsFor(config, 'stoneSettings')} value={st.setting} onPick={(v) => ctl.toggleStone('setting', v)} />
      </Field>
      <Field label="Package" hint="Ties straight to pricing.">
        <ChipRow options={optionsFor(config, 'stonePackages')} value={st.package} onPick={(v) => ctl.setStones({ package: v })} />
      </Field>
      <p className={styles.note}>Stone field color follows the team colors set in step 10.</p>
    </>
  )
}

function MetalStep({ c, ctl, config, context }) {
  const m = c.metal || {}
  const colors = context.school?.colors || []
  // The dropper sets a custom VIBRANTIUM color. Solid gold is the color it is,
  // so on a karat gold the control is replaced by the reason rather than
  // offered and ignored — or worse, posted.
  const droppable = allowsVibrantiumId(m.tone)
  return (
    <>
      {/* THE SIX SR METALS — two Vibrantium compositions and the four solid
          karat golds. The value posted is the metal's exact name, because that
          is the string the engine renders into the prompt. */}
      <Field label="Metal">
        <ChipRow options={optionsFor(config, 'metalTones')} value={m.tone} onPick={(v) => ctl.setMetal({ tone: v })} />
      </Field>
      <Field label="Finish">
        <ChipRow options={optionsFor(config, 'metalFinishes')} value={m.finish} onPick={(v) => ctl.setMetal({ finish: v })} />
      </Field>
      <Field
        label={VIBRANTIUM_ID_LABEL}
        hint={droppable
          ? 'The rainbow dropper pins an exact Vibrantium color instead of a described one. Blank means the metal above stands on its own.'
          : undefined}
      >
        {droppable ? (
          <div className={styles.dropper}>
            <input
              type="color"
              className={styles.colorInput}
              value={/^#[0-9a-f]{6}$/i.test(m.vibrantiumId || '') ? m.vibrantiumId : '#8b6f2a'}
              onChange={(e) => ctl.setMetal({ vibrantiumId: e.target.value.toUpperCase() })}
              aria-label="Vibrantium ID color dropper"
            />
            <input
              className="input"
              value={m.vibrantiumId || ''}
              onChange={(e) => ctl.setMetal({ vibrantiumId: e.target.value })}
              placeholder="#RRGGBB"
            />
            {m.vibrantiumId && <button type="button" className={styles.linkBtn} onClick={() => ctl.setMetal({ vibrantiumId: '' })}>Clear</button>}
          </div>
        ) : (
          <p className={styles.note}>
            {m.tone
              ? `A Vibrantium ID is a custom color for a Vibrantium piece. ${m.tone} is a solid precious metal, so it has no color override.`
              : 'Pick a metal above. A Vibrantium ID applies to the Vibrantium compositions.'}
          </p>
        )}
      </Field>

      <Field label="Team colors" hint="Stone field color and the channel sets follow these. Pulled from the school's verified brand kit — included only when you click.">
        <div className={styles.chipRow}>
          {colors.length ? colors.map((col, i) => (
            <button
              key={col.hex + i}
              type="button"
              className={`${styles.opt} ${(c.teamColor || []).includes(col.hex) ? styles.optOn : ''}`}
              onClick={() => ctl.toggleTeamColor(col.hex)}
            >
              <span className={styles.swatchDot} style={{ background: col.hex }} />
              {col.hex}
            </button>
          )) : <span className={styles.pickerEmpty}>No verified brand colors on file — add them by hand below.</span>}
        </div>
        <div className={styles.fallbackRow}>
          <input
            className="input"
            placeholder="Add a color — hex or a name like “teal”"
            onKeyDown={(e) => { if (e.key === 'Enter' && e.currentTarget.value.trim()) { e.preventDefault(); ctl.toggleTeamColor(e.currentTarget.value.trim()); e.currentTarget.value = '' } }}
          />
        </div>
        {!!(c.teamColor || []).length && (
          <p className={styles.note}>Included: {c.teamColor.join(', ')}</p>
        )}
      </Field>
    </>
  )
}

/* ---------------------------------------------------------------- *
 * Step 13 — the presentation, where the Locked Layer is enforced
 * ---------------------------------------------------------------- */

function BackgroundStep({ ctl, context, c, k }) {
  /* SUGGEST, DON'T AUTOFILL — and say where it came from.
   *
   * The presentation line already had a one-click suggestion here; it did not
   * name its SOURCE, and `audience` had no chip at all even though
   * `suggestAttributes()` has always composed one. Both now go through the same
   * `Suggestions` primitive step 4 uses, so a chip on this step reads exactly
   * like a chip on that one — label, value, and `from <source>` — and neither
   * writes anything until it is clicked. */
  const suggestions = useMemo(() => suggestAttributes(context), [context])
  const applied = (s) => k[s.field] === s.value
  const apply = (s) => ctl.setKeyed(s.field, s.value)
  const rows = suggestions.filter((s) => s.field === 'presentationLine' || s.field === 'audience')
  return (
    <>
      <CanvaFolderPicker
        folderKey="backgrounds"
        value={c.background}
        onChange={(v) => ctl.setComponent('background', v)}
        label="Background"
      />

      {/* DEFAULTED, NOT ASKED FOR. Composed from the championship and school
          records as soon as both are picked (Studio.jsx), and editable in
          place. Once it is edited, nothing refills it. */}
      <Field label="Presentation line" hint="Composed from the record as “School – Sport – Championship – Year”. Edit it freely.">
        <input
          className="input"
          value={k.presentationLine}
          onChange={(e) => ctl.setKeyed('presentationLine', e.target.value)}
          placeholder="e.g. Coastal Carolina University Baseball 2026 CWS Finalist"
        />
      </Field>

      <Field label="Presented to" hint="Every presentation carries an audience.">
        <input className="input" value={k.audience} onChange={(e) => ctl.setKeyed('audience', e.target.value)} placeholder="e.g. the Athletic Director" />
      </Field>

      <Suggestions rows={rows} applied={applied} apply={apply} label="Presentation suggestions" />

      <p className={styles.lockNote}>
        The SR logo is on this presentation whether or not it is keyed here — it is part of the Locked Layer, not an option.
      </p>
    </>
  )
}

/**
 * THE BEZEL DROP, AS A SOURCE RATHER THAN A SECOND FIELD.
 *
 * Brian: *"We also need the bezel drop as an option."*
 *
 * A drop IS a bezel — so it writes the SAME `components.bezel` the curated
 * library writes, and the render contract needs no new field. Making it a
 * separate payload key would have meant inventing a slot the engine drops on
 * the floor, and given the operator two bezels to reconcile.
 *
 * ONE DESTINATION, TWO LIBRARIES. Switching source does NOT clear the pick:
 * a rep who has chosen a drop and then looks at the curated set to compare
 * must not lose the drop for looking. The chosen design's own label is shown
 * above the switch so it is never ambiguous which one is live.
 *
 * Measured 2026-09-09: the drop library holds 11 sport sub-folders with real
 * versioned drops. It was empty when it was last written about; it is not now.
 */
function BezelSourcePicker({ c, ctl, step }) {
  const [source, setSource] = useState(BEZEL_SOURCES[0].key)
  const active = BEZEL_SOURCES.find((s) => s.key === source) || BEZEL_SOURCES[0]
  return (
    <>
      <Field label="Where the bezel comes from" hint="Both libraries fill the same bezel slot — switching source never clears what you already picked.">
        <div className={styles.chipRow}>
          {BEZEL_SOURCES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`${styles.opt} ${source === s.key ? styles.optOn : ''}`}
              onClick={() => setSource(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <p className={styles.note}>{active.note}</p>
      </Field>
      <CanvaFolderPicker
        folderKey={active.key}
        value={c[step.key]}
        onChange={(v) => ctl.setComponent(step.key, v)}
        label={step.title}
      />
    </>
  )
}
